import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MaterialPreview } from "@/components/MaterialPreview";
import { EmptyState, fmtSize } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, FileText, Download, Eye, ExternalLink, Link2, Loader2,
  BookOpen, ClipboardCheck, Boxes, UserCog,
} from "lucide-react";

const CATEGORY_META = {
  Onboarding: { icon: BookOpen, blurb: "Orientation & first-day essentials" },
  SOPs: { icon: ClipboardCheck, blurb: "Standard operating procedures" },
  "Training Packets": { icon: Boxes, blurb: "Guides & practice packets" },
};

function MaterialCard({ m, onPreview }) {
  const download = async () => {
    try {
      const res = (await api.get(`/materials/${m.id}/download`)).data;
      window.open(res.external ? res.url : `${BACKEND_URL}${res.url}`, "_blank");
    } catch { toast.error("Download failed"); }
  };
  return (
    <Card className="p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`scoped-material-card-${m.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center h-11 w-11 rounded-lg bg-accent/15 text-accent shrink-0">
          {m.source_type === "link" ? <Link2 size={20} /> : <FileText size={20} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate flex items-center gap-1.5">
            {m.title}
            {m.source_type === "link" && <ExternalLink size={12} className="text-muted-foreground shrink-0" />}
          </div>
          <div className="text-[11px] text-muted-foreground uppercase font-mono">
            {m.source_type === "link" ? "Google Drive link" : `${m.file_extension} · ${fmtSize(m.file_size)}`}
          </div>
        </div>
      </div>
      {m.description && <p className="text-xs text-muted-foreground line-clamp-2">{m.description}</p>}
      <div className="flex gap-2 pt-1 mt-auto">
        {m.source_type === "link" ? (
          <Button size="sm" variant="secondary" className="flex-1 gap-1" onClick={() => window.open(m.external_url, "_blank")} data-testid={`scoped-open-${m.id}`}><ExternalLink size={14} /> Open</Button>
        ) : (
          <>
            <Button size="sm" variant="secondary" className="flex-1 gap-1" onClick={() => onPreview(m.id)} data-testid={`scoped-preview-${m.id}`}><Eye size={14} /> Preview</Button>
            <Button size="sm" variant="ghost" className="gap-1" onClick={download} data-testid={`scoped-download-${m.id}`}><Download size={14} /></Button>
          </>
        )}
      </div>
    </Card>
  );
}

export function ScopedLibrary({ asEmployee = false }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [previewId, setPreviewId] = useState(null);

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const categories = settings?.material_categories || ["Onboarding", "SOPs", "Training Packets"];

  const params = {};
  if (asEmployee) params.as_employee = true;
  if (q) params.q = q;
  const { data: materials = [], isLoading } = useQuery({
    queryKey: ["scoped-materials", params],
    queryFn: async () => (await api.get("/materials", { params })).data,
  });

  if (!user?.position) {
    return (
      <Card data-testid="scoped-no-position">
        <EmptyState icon={UserCog} title="No position assigned yet"
          desc="Ask an admin to set your position so your Onboarding, SOPs and Training Packets appear here." />
      </Card>
    );
  }

  const byCategory = (cat) => materials.filter((m) => m.category === cat);

  return (
    <div className="space-y-5" data-testid="scoped-library">
      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium">Your position: <span className="text-accent">{user.position}</span></p>
            <p className="text-xs text-muted-foreground mt-0.5">These are the materials assigned to your position.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input placeholder="Search your materials…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="scoped-library-search" />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-6">
          {categories.map((cat) => {
            const Icon = CATEGORY_META[cat]?.icon || FileText;
            const items = byCategory(cat);
            return (
              <div key={cat} data-testid={`scoped-category-${cat}`}>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-primary/10 text-primary"><Icon size={18} /></div>
                  <div>
                    <h3 className="font-heading font-bold leading-none">{cat}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{CATEGORY_META[cat]?.blurb}</p>
                  </div>
                  <span className="ml-auto text-xs font-mono text-muted-foreground">{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <p className="text-sm text-muted-foreground border border-dashed rounded-xl px-4 py-6 text-center">Nothing here yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((m) => <MaterialCard key={m.id} m={m} onPreview={setPreviewId} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MaterialPreview materialId={previewId} open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)} />
    </div>
  );
}
